import React, { type FC, type PropsWithChildren } from 'react'
import { Box, Table as MuiTable, TableBody as MuiTableBody, type SxProps, TableContainer, type Theme } from '@mui/material'

import { isArrayNotEmpty } from '../../../../utils'

import { extractDataTestId } from '../../utils'
import { EmptyTableBody } from './Empty'
import { Footer } from './Footer'
import { Header } from './Header'
import { LoadingTableBody } from './Loading'
import { Row } from './Row'
import { PropsProvider, useHeight, useLoading, useVisibleRows } from './State'
import { Toolbar } from './Toolbar'
import type { Line, Props } from './Types'

const containerStyle: SxProps<Theme> = { borderRadius: 1 }
const Container: FC<PropsWithChildren> = ({ children }) => {
    const maxHeight = useHeight()

    return <TableContainer sx={[{ maxHeight }, containerStyle]}>{children}</TableContainer>
}

const LoadedBodyContent: FC = () => {
    const visibleRows = useVisibleRows()

    if (!isArrayNotEmpty(visibleRows)) {
        return <EmptyTableBody />
    }

    return (
        <>
            {visibleRows.map((row, rowIndex) => (
                <Row key={rowIndex} index={rowIndex} row={row} />
            ))}
        </>
    )
}

const BodyContent: FC = () => {
    const loading = useLoading()

    return loading ? <LoadingTableBody loading={loading} /> : <LoadedBodyContent />
}

export const Table = <L extends Line,>({ children, ...props }: PropsWithChildren<Props<L>>): ReturnType<FC> => (
    <PropsProvider value={props}>
        <Box data-testid={extractDataTestId(props) || 'common-table'}>
            <Toolbar />
            <Container>
                <MuiTable stickyHeader size="medium">
                    <Header />
                    <MuiTableBody>
                        <BodyContent />
                    </MuiTableBody>
                </MuiTable>
                <Footer />
            </Container>
            {children}
        </Box>
    </PropsProvider>
)
